/** Initial limits from the plan. Server-info reports the effective values. */
export const LIMITS = {
  /**
   * 2047 MiB per file (shown as "2GB"), 10 files per send. ClamAV 1.4 scans files up to 2 GiB − 1 byte,
   * so a full 2 GiB attachment could never be "정상".
   */
  maxFileBytes: 2047 * 1024 * 1024,
  maxFilesPerMessage: 10,
  /** "저장한 메시지" per person. */
  maxSavedMessages: 1000,
  /** "예약 전송": waiting per person, and how far ahead. */
  maxScheduledMessages: 100,
  scheduleAheadDays: 30,
  /** concurrent uploads per user / per VM. */
  maxConcurrentUploadsPerUser: 2,
  maxConcurrentUploadsPerInstance: 8,
  /** A profile photo as picked or uploaded: a few MiB and at most 40 megapixels (decoded before anything is stored). */
  maxAvatarBytes: 5 * 1024 * 1024,
  maxAvatarPixels: 40_000_000,
  maxMessageChars: 10_000,
  /** Different emoji on one message; adding one more of an emoji already there is always allowed. */
  maxReactionsPerMessage: 20,
  /** Reactors named in a message's reaction summary per emoji; the rest come from messages.reactions. */
  reactionPreviewUsers: 10,
  maxGroupTitleChars: 100,
  maxGroupMembers: 500,
  maxInviteBatch: 50,
  /** invitation lifetime. */
  inviteTtlDays: 7,
  /** default own-message edit/delete window and deletion grace period. */
  defaultEditWindow: '24h',
  deletedContentGraceDays: 30,
  /** event replay retention before a snapshot refetch is required. */
  eventRetentionDays: 7,
  /** Search: ≤ 2 characters is a "short query" limited to recent results. */
  shortQueryMaxChars: 2,
  shortQuerySinceDays: 30,
  shortQueryMaxResults: 50,
  /** Short-lived desktop access token; the refresh token stays in the main process. */
  accessTokenTtlSeconds: 15 * 60,
  refreshTokenTtlDays: 30,
  /** A web session (and the pushes to that browser) lasts this long after it was last used, renewed while in use. */
  webSessionIdleDays: 90,
} as const;

/**
 * Kinds the apps show and the server checks against the bytes (a .pdf must be a PDF). Any other
 * name is an 'other' file: sent as it is, served only as a download. SVG is a download too.
 */
export const KNOWN_EXTENSIONS = {
  document: ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'hwp', 'hwpx', 'txt', 'md', 'csv', 'rtf', 'odt', 'ods', 'odp', 'key', 'pages', 'numbers'],
  image: ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'bmp', 'svg'],
  archive: ['zip', '7z', 'tar', 'gz', 'tgz'],
} as const;

export type FileKind = keyof typeof KNOWN_EXTENSIONS | 'other';

/**
 * Programs, installers, disk images and scripts that run when opened: the types Gmail refuses
 * (support.google.com/mail/answer/6590), with the macOS and Linux installers. Every other type may
 * be sent; the server also refuses a program by its bytes, whatever its name.
 */
export const BLOCKED_EXTENSIONS: ReadonlySet<string> = new Set([
  'ade', 'adp', 'apk', 'appx', 'appxbundle', 'bat', 'cab', 'chm', 'cmd', 'com', 'cpl', 'diagcab', 'diagcfg', 'diagpack',
  'dll', 'dmg', 'ex', 'ex_', 'exe', 'hta', 'img', 'ins', 'iso', 'isp', 'jar', 'jnlp', 'js', 'jse', 'lib', 'lnk', 'mde',
  'msc', 'msi', 'msix', 'msixbundle', 'msp', 'mst', 'nsh', 'pif', 'ps1', 'scr', 'sct', 'shb', 'sys', 'vb', 'vbe', 'vbs',
  'vhd', 'vxd', 'wsc', 'wsf', 'wsh', 'xll',
  'appimage', 'command', 'deb', 'mpkg', 'pkg', 'rpm', 'run',
]);

/**
 * A file name as people should read it: invisible format characters removed (bidi overrides
 * that make "보고서\u202Efdp.xls" read as "보고서slx.pdf", zero-width spaces) and in NFC, so
 * names from macOS (NFD) compare and search like the rest.
 */
export function cleanFileName(fileName: string): string {
  return fileName.replace(/\p{Cf}/gu, '').normalize('NFC');
}

export function extensionOf(fileName: string): string {
  const base = fileName.split(/[\\/]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

export function fileKindOf(fileName: string): FileKind {
  const ext = extensionOf(fileName);
  for (const [kind, list] of Object.entries(KNOWN_EXTENSIONS) as [FileKind, readonly string[]][]) {
    if (list.includes(ext)) return kind;
  }
  return 'other';
}

export function isAllowedAttachment(fileName: string): boolean {
  return !BLOCKED_EXTENSIONS.has(extensionOf(fileName));
}

/** Images that may be previewed inline (never SVG). */
export function isInlinePreviewable(fileName: string): boolean {
  return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extensionOf(fileName));
}

/** Video and audio the browsers play (MP4/MOV with H.264 and AAC, WebM, MP3, M4A, WAV, Ogg, FLAC). */
const PLAYABLE_EXTENSIONS = {
  video: ['mp4', 'm4v', 'mov', 'webm'],
  audio: ['mp3', 'm4a', 'aac', 'wav', 'ogg', 'oga', 'opus', 'flac'],
} as const;

/** 'video' or 'audio' when the app may play the file in place (the server also checks its bytes); null otherwise. */
export function inlinePlayback(fileName: string): 'video' | 'audio' | null {
  const ext = extensionOf(fileName);
  if ((PLAYABLE_EXTENSIONS.video as readonly string[]).includes(ext)) return 'video';
  if ((PLAYABLE_EXTENSIONS.audio as readonly string[]).includes(ext)) return 'audio';
  return null;
}
